import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Copy, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ComparativoSimilares from '../similares/ComparativoSimilares';
import ComparativoCliente from '../similares/ComparativoCliente';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const copiar = (texto, msg) => {
  navigator.clipboard.writeText(texto || '').then(() => toast.success(msg || 'Copiado!'));
};

/**
 * Modal "Propor Similar" para a tela de Atendimento.
 * Reaproveita o fluxo do AES: localiza similares (estoque) e gera os textos padrão.
 * Props: { pedido (pedidoErp), onClose }
 */
export default function ProporSimilarModal({ pedido, onClose }) {
  const { getAuthHeader } = useAuth();
  const produto = pedido?.produto || '[produto]';
  const entrega = pedido?.numero_pedido || '[entrega]';
  const skuOriginal = pedido?.codigo_item_vtex || '';

  const [loading, setLoading] = useState(true);
  const [propostos, setPropostos] = useState([]);   // [{sku,nome,id_bseller,xd,ufs,alerta_filial}]
  const [ufEntrega, setUfEntrega] = useState(null);
  const [precoOriginal, setPrecoOriginal] = useState(0);
  const [idOriginal, setIdOriginal] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [imgMap, setImgMap] = useState({});
  const [linkMap, setLinkMap] = useState({});   // cod_terceiro -> cadastro na VTEX
  const [lojaMap, setLojaMap] = useState({});   // cod_terceiro -> página na loja VTEX (clique na foto)
  const [comparativo, setComparativo] = useState(false);   // imagem para o cliente
  // Resposta CRUA do endpoint (original + propostos completos). A lista `propostos`
  // abaixo é uma versão reduzida, feita para montar os textos; a tabela comparativa
  // precisa dos campos que a redução descarta (EAN, custo, subcategoria, estoque
  // por filial). Guardar os dois evita mexer na geração das mensagens.
  const [bruto, setBruto] = useState(null);

  // Busca similares (mesmo endpoint do AES: /produtos/sugerir-similar)
  useEffect(() => {
    let ativo = true;
    setLoading(true);
    axios.get(`${API_URL}/api/produtos/sugerir-similar`, {
      params: { sku: skuOriginal, entrega: String(entrega).split('.')[0], max_propostos: 5 },
      headers: getAuthHeader(),
    }).then(r => {
      if (!ativo) return;
      setBruto(r.data || null);
      const lista = (r.data?.propostos || []).map(p => ({
        sku: p.cod_terceiro,
        nome: p.descricao,
        id_bseller: p.id_item_bseller ? String(p.id_item_bseller) : '',
        xd: p.xd_total || 0,
        ufs: p.ufs_com_estoque || [],
        alerta_filial: p.alerta_filial || null,
        // Valor: já vinha do endpoint, a tela é que descartava. Sem o preço na
        // frente a atendente escolhia no escuro e só descobria a diferença na
        // tabela de comparação (pedido da Adneia, 02/09).
        preco: Number(p.preco_venda || 0),
        venda_pct: p.venda_pct ?? null,
        venda_situacao: p.venda_situacao || null,
        pecas: p.pecas ?? null,
      }));
      setPropostos(lista);
      setUfEntrega(r.data?.uf_entrega || null);
      setPrecoOriginal(Number(r.data?.original?.preco_venda || 0));
      // ID BSeller do original — entra no texto junto do nome (pedido da Adneia, 15/09).
      setIdOriginal(r.data?.original?.id_item_bseller ? String(r.data.original.id_item_bseller) : '');
      setSel(new Set(lista.map(s => s.sku)));  // por padrão, todos selecionados
    }).catch(() => {
      if (ativo) { setPropostos([]); setBruto(null); }
    }).finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [skuOriginal, entrega, getAuthHeader]);

  // Busca lazy das imagens (original + similares)
  useEffect(() => {
    const skus = [skuOriginal, ...propostos.map(p => p.sku)].filter(Boolean);
    skus.filter(sk => imgMap[sk] === undefined).forEach(sk => {
      axios.get(`${API_URL}/api/produtos/imagem/${encodeURIComponent(sk)}`, { headers: getAuthHeader() })
        .then(r => {
          setImgMap(prev => ({ ...prev, [sk]: r.data?.image_url || '' }));
          if (r.data?.vtex_admin_url) setLinkMap(prev => ({ ...prev, [sk]: r.data.vtex_admin_url }));
          if (r.data?.vtex_loja_url) setLojaMap(prev => ({ ...prev, [sk]: r.data.vtex_loja_url }));
        })
        .catch(() => setImgMap(prev => ({ ...prev, [sk]: '' })));
    });
  }, [propostos, skuOriginal, getAuthHeader]); // eslint-disable-line

  const toggle = (sku) => setSel(prev => {
    const n = new Set(prev); n.has(sku) ? n.delete(sku) : n.add(sku); return n;
  });

  const escolhidos = propostos.filter(p => sel.has(p.sku));
  const multi = escolhidos.length > 1;
  const linkOriginal = imgMap[skuOriginal] || '';
  const sufixo = (url) => url ? ` - ${url}` : '';
  const fmtBRL = (v) => (Number(v) > 0
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null);
  const fmtSim = (s) => (s.id_bseller ? `${s.nome} (ID: ${s.id_bseller})` : s.nome);
  const produtoComId = idOriginal ? `${produto} (${idOriginal})` : produto;
  const fmtSimLink = (s) => `${fmtSim(s)}${sufixo(imgMap[s.sku] || '')}`;

  const blocoInicial = multi
    ? `Temos como alternativa os seguintes itens similares:\n${escolhidos.map((s, i) => `${i + 1}) ${fmtSimLink(s)}`).join('\n')}\nPoderia confirmar se aceita a substituição por um deles?`
    : (escolhidos[0]
        ? `Temos como alternativa um item similar: ${fmtSimLink(escolhidos[0])}. Poderia confirmar se aceita a substituição pelo item similar?`
        : 'Poderia confirmar se aceita a substituição por um item similar?');
  const blocoAceita = multi
    ? `os novos itens:\n${escolhidos.map((s, i) => `${i + 1}) ${fmtSimLink(s)}`).join('\n')}`
    : (escolhidos[0] ? `o novo item - ${fmtSimLink(escolhidos[0])}` : 'o novo item');

  const templates = [
    {
      label: 'Mensagem inicial', emoji: '💬',
      texto: `Boa tarde\nInfelizmente, tivemos uma falha sistêmica no item ${produtoComId} - ${entrega}${sufixo(linkOriginal)}\n${blocoInicial}\nAguardamos retorno e seguimos à disposição.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: 'Cliente não aceita', emoji: '❌',
      texto: `Agradecemos a confirmação, Iremos acionar o canal de troca/venda para estornar os valores pagos. Nossas sinceras desculpas pelo ocorrido.\nAtenciosamente!\nAtendimento Weconnect`,
    },
    {
      label: 'Cliente aceita', emoji: '✅',
      texto: `Agradecemos a confirmação, seguiremos com a preparação d${multi ? 'os' : 'o'} ${blocoAceita}.\nNossas sinceras desculpas pelo ocorrido.\nAtenciosamente!\nAtendimento Weconnect`,
    },
  ];

  const foneDigits = (pedido?.fone_cliente || '').replace(/\D/g, '');
  const wa = foneDigits ? (foneDigits.length <= 11 ? `55${foneDigits}` : foneDigits) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl mx-4 p-5 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2"><span>🔄</span> Propor Similar — sem estoque</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2 space-y-0.5">
          <div>
            <strong>Produto:</strong> {produto} <span className="text-slate-400">({skuOriginal})</span>
            {fmtBRL(precoOriginal) && <span className="ml-1 font-semibold text-slate-700 dark:text-slate-200">· {fmtBRL(precoOriginal)}</span>}
          </div>
          <div><strong>Entrega:</strong> {entrega}{ufEntrega ? ` · UF ${ufEntrega}` : ''}</div>
          {pedido?.canal_vendas && <div><strong>Canal:</strong> {pedido.canal_vendas}</div>}
          {pedido?.nome_cliente && <div><strong>Cliente:</strong> {pedido.nome_cliente}</div>}
          {wa && (
            <div className="flex items-center gap-2">
              <strong>Telefone:</strong>
              <button onClick={() => copiar(pedido.fone_cliente, 'Telefone copiado!')} className="font-mono hover:text-blue-600 hover:underline">{pedido.fone_cliente}</button>
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-300 hover:bg-green-200 text-[11px] font-semibold">📱 WhatsApp</a>
            </div>
          )}
        </div>

        {/* Regra do canal (NiceQuest nao sai de SP) e aviso do motor. Vem do backend e
            ficam ANTES da comparacao: a regra tem de ser lida antes de escolher o
            similar, nao depois. O aviso_fallback ja existia no endpoint e este modal
            simplesmente engolia — inclusive o "nenhum similar tem estoque fora de SP". */}
        {bruto?.nota_canal && (
          <div className="mb-3 rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800 px-3 py-2 text-xs font-semibold text-orange-900 dark:text-orange-200 flex items-start gap-1.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{bruto.nota_canal}</span>
          </div>
        )}
        {bruto?.aviso_fallback && !loading && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{bruto.aviso_fallback}</span>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center animate-pulse">Localizando similares com estoque...</div>
        ) : propostos.length === 0 ? (
          <div className="text-sm py-6 text-center text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
            Nenhum similar com estoque encontrado para este item.
          </div>
        ) : (
          <>
            {/* Comparação Original x Propostos — mesma tabela usada no modal de
                Cancelamentos (componente compartilhado). Substituiu a lista compacta
                com miniaturas: sem EAN, custo, subcategoria e estoque por filial a
                atendente escolhia sem os dados que decidem (pedido da Adnéia, 28/09).
                A seleção continua sendo a mesma (`sel`), que alimenta os textos abaixo. */}
            <div className="overflow-x-auto">
              <ComparativoSimilares
                original={bruto?.original}
                propostos={bruto?.propostos || []}
                selecionados={sel}
                onToggle={toggle}
                imgMap={imgMap}
                linkMap={linkMap}
                lojaMap={lojaMap}
              />
            </div>

            {/* Textos padrão */}
            <div className="space-y-3">
              {/* Imagem do comparativo para anexar no WhatsApp: original x os similares marcados */}
              <button onClick={() => setComparativo(true)} disabled={!skuOriginal || escolhidos.length === 0}
                title={escolhidos.length ? 'Gerar a imagem com o original e os similares marcados' : 'Marque ao menos um similar'}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold border border-blue-300 disabled:opacity-50">
                🖼️ Imagem do comparativo
              </button>
              {templates.map((t, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-sm">{t.emoji} {t.label}</span>
                    <button onClick={() => copiar(t.texto, `"${t.label}" copiado!`)} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 hover:bg-green-200 font-semibold border border-green-300">
                      <Copy className="h-3 w-3" /> Copiar
                    </button>
                  </div>
                  <pre className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 rounded p-2 font-sans leading-relaxed">{t.texto}</pre>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {comparativo && (
        <ComparativoCliente
          original={{ sku: skuOriginal, nome: produto, id: idOriginal }}
          similares={escolhidos.map(s => ({ sku: s.sku, nome: s.nome, id: s.id_bseller }))}
          entrega={pedido?.numero_pedido || ''}
          onClose={() => setComparativo(false)}
        />
      )}
    </div>
  );
}
